import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Row,
  Col,
  Typography,
  Card,
  Space,
  Divider,
  Alert
} from 'antd';
import { StatusIndicator } from './StatusIndicator';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface ReportData {
  id: string;
  batchId: string;
  companyName: string;
  businessName: string;
  elementName: string;
  actualValue: number;
  unit: string;
  cityLevel: number;
  responsiblePerson: string;
  checkDate: string;
  referenceRange: string;
  evaluation: {
    status: string;
    referenceRange: string;
    evaluation?: string; // 可选，与主组件保持一致
  };
  remarks?: string;
  createTime: string;
}

interface EditableFields {
  actualValue: number;
  evaluation: string;
  remarks: string;
}

interface EnhancedEditModalProps {
  visible: boolean;
  data: ReportData | null;
  onOk: (values: EditableFields) => void;
  onCancel: () => void;
}

const EnhancedEditModal: React.FC<EnhancedEditModalProps> = ({
  visible,
  data,
  onOk,
  onCancel
}) => {
  const [form] = Form.useForm();
  const [currentActualValue, setCurrentActualValue] = useState<number>(0);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (data && visible) {
      const initialValues = {
        actualValue: data.actualValue,
        evaluation: data.evaluation?.evaluation || '',
        remarks: data.remarks || ''
      };
      form.setFieldsValue(initialValues);
      setCurrentActualValue(data.actualValue);
      setShowPreview(false);
    }
  }, [data, visible, form]);

  const handleActualValueChange = (value: number | null) => {
    if (value !== null && !isNaN(value)) {
      setCurrentActualValue(value);
      setShowPreview(value !== data?.actualValue);
    }
  };

  const handleOk = () => {
    form.validateFields().then(values => {
      onOk(values);
      form.resetFields();
      setShowPreview(false);
    });
  };

  const handleCancel = () => {
    onCancel();
    form.resetFields();
    setShowPreview(false);
  };

  if (!data) return null;

  return (
    <Modal
      title="编辑检查报告"
      open={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      width={800}
      okText="保存"
      cancelText="取消"
    >
      <div>
        {/* 基本信息展示 */}
        <Card size="small" style={{ marginBottom: '16px' }}>
          <Title level={5} style={{ margin: '0 0 12px 0' }}>基本信息</Title>
          <Row gutter={[16, 8]}>
            <Col span={12}>
              <Text type="secondary">业务所属创大：</Text>
              <Text strong>{data.companyName}</Text>
            </Col>
            <Col span={12}>
              <Text type="secondary">培训业务名称：</Text>
              <Text strong>{data.businessName}</Text>
            </Col>
            <Col span={12}>
              <Text type="secondary">培训要素：</Text>
              <Text strong>{data.elementName}</Text>
            </Col>
            <Col span={12}>
              <Text type="secondary">城市等级：</Text>
              <Text strong>{data.cityLevel}级</Text>
            </Col>
            <Col span={12}>
              <Text type="secondary">参考范围：</Text>
              <Text strong>{data.referenceRange}</Text>
            </Col>
            <Col span={12}>
              <Text type="secondary">检查时间：</Text>
              <Text strong>{data.checkDate}</Text>
            </Col>
          </Row>
        </Card>

        {/* 当前状态 */}
        <Card size="small" style={{ marginBottom: '16px' }}>
          <Title level={5} style={{ margin: '0 0 12px 0' }}>当前状态</Title>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Row gutter={16} align="middle">
              <Col span={8}>
                <Text type="secondary">检查结果：</Text>
                <Text strong>{data.actualValue} {data.unit}</Text>
              </Col>
              <Col span={16}>
                <Text type="secondary">状态：</Text>
                <StatusIndicator
                  actualValue={data.actualValue}
                  referenceRange={data.referenceRange}
                  elementName={data.elementName}
                  cityLevel={data.cityLevel}
                  evaluation={data.evaluation?.evaluation}
                  size="medium"
                />
              </Col>
            </Row>
          </Space>
        </Card>

        {/* 编辑表单 */}
        <Form form={form} layout="vertical">
          <Title level={5} style={{ margin: '0 0 12px 0' }}>编辑信息</Title>

          <Form.Item
            label="检查结果"
            name="actualValue"
            rules={[
              { required: true, message: '请输入检查结果' },
              { type: 'number', min: 0, message: '检查结果必须大于等于0' }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="请输入检查结果"
              addonAfter={data.unit}
              precision={2}
              onChange={handleActualValueChange}
            />
          </Form.Item>

          {/* 实时状态预览 */}
          {showPreview && (
            <Card size="small" style={{ marginBottom: '16px', border: '2px dashed #1890ff' }}>
              <Alert
                message="实时预览"
                description={
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Text>修改后的状态预览：</Text>
                    <StatusIndicator
                      actualValue={currentActualValue}
                      referenceRange={data.referenceRange}
                      elementName={data.elementName}
                      cityLevel={data.cityLevel}
                      size="medium"
                      showAnimation={true}
                    />
                  </Space>
                }
                type="info"
                showIcon
              />
            </Card>
          )}

          <Form.Item
            label="评定信息"
            name="evaluation"
            rules={[{ required: true, message: '请输入评定信息' }]}
          >
            <Input placeholder="请输入评定信息" />
          </Form.Item>

          <Form.Item
            label="备注"
            name="remarks"
          >
            <TextArea
              rows={4}
              placeholder="请输入备注信息"
              showCount
              maxLength={500}
            />
          </Form.Item>
        </Form>

        {/* 操作说明 */}
        <Divider />
        <Alert
          message="操作说明"
          description={
            <ul style={{ marginBottom: 0, paddingLeft: '20px' }}>
              <li>修改检查结果时，系统会实时计算并预览新的状态</li>
              <li>状态计算基于参考范围和培训要素类型自动判断</li>
              <li>人均课时费在一二线城市仅与参考最大值比较</li>
              <li>保存后将更新表格中的显示状态</li>
            </ul>
          }
          type="info"
          showIcon
          style={{ marginTop: '8px' }}
        />
      </div>
    </Modal>
  );
};

export default EnhancedEditModal;